import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import { type AnalyticsConfig, analyticsService, defaultSessionReplaySampleRate, extractAttributionTraits, hasAttributionParams, type ScreenViewProperties } from "@medbillai/app/lib/analytics";
export function useAnalyticsService() {
  const {
    pathname,
    query
  } = useRouter();
  // Track if we've already captured attribution for this session
  const attributionCapturedRef = useRef(false);

  // Initialize analytics service on mount
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY && process.env.NEXT_PUBLIC_RUDDERSTACK_DATA_PLANE_URL) {
      const config: AnalyticsConfig = {
        writeKey: process.env.NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY,
        dataPlaneUrl: process.env.NEXT_PUBLIC_RUDDERSTACK_DATA_PLANE_URL,
        debug: process.env.NEXT_PUBLIC_RUDDERSTACK_DEBUG === "true",
        amplitudeApiKey: process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY,
        sessionReplay: {
          enabled: process.env.NEXT_PUBLIC_SESSION_REPLAY_ENABLED === "true",
          sampleRate: process.env.NEXT_PUBLIC_SESSION_REPLAY_SAMPLE_RATE != null ? parseFloat(process.env.NEXT_PUBLIC_SESSION_REPLAY_SAMPLE_RATE) : defaultSessionReplaySampleRate
        }
      };
      void analyticsService.initialize(config);
    } else {
      console.error("Analytics configuration is missing.");
    }
  }, []);

  // Track screen view and handle anonymousId and attribution whenever pathname or query changes
  useEffect(() => {
    // Set anonymousId if it exists in URL query params
    if (query.anonymous_id && typeof query.anonymous_id === "string") {
      analyticsService.setAnonymousId(query.anonymous_id);
    }

    // Capture attribution parameters if present and not already captured this session
    // Only capture on first page load to avoid overwriting attribution on
    // internal navigation if parameters get corrupted
    if (!attributionCapturedRef.current && hasAttributionParams(query)) {
      const attributionTraits = extractAttributionTraits(query);
      analyticsService.identifyAnonymousUser(attributionTraits);
      attributionCapturedRef.current = true;
    }

    // Track screen view if pathname exists
    if (pathname) {
      analyticsService.trackScreenView(pathname, query as ScreenViewProperties);
    }
  }, [pathname, query]);
}